import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Loader } from "components/Loader";
import { customAPI, customGetAPI } from "services/utils/CustomAPI";
import * as Mui from "@mui/material";
import AccessTimeOutlined from "@mui/icons-material/AccessTimeOutlined";
import AddRounded from "@mui/icons-material/AddRounded";
import ApartmentOutlined from "@mui/icons-material/ApartmentOutlined";
import ArrowForwardRounded from "@mui/icons-material/ArrowForwardRounded";
import BoltOutlined from "@mui/icons-material/BoltOutlined";
import EditOutlined from "@mui/icons-material/EditOutlined";
import GroupsOutlined from "@mui/icons-material/GroupsOutlined";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import LightbulbOutlined from "@mui/icons-material/LightbulbOutlined";
import LockOutlined from "@mui/icons-material/LockOutlined";
import RemoveRounded from "@mui/icons-material/RemoveRounded";
import SaveOutlined from "@mui/icons-material/SaveOutlined";
import SettingsOutlined from "@mui/icons-material/SettingsOutlined";
import {
  designColors,
  designRadii,
  designShell,
  designTheme,
  designType,
  HEADER_ACTION_BUTTON_WIDTH,
} from "services/constants/designTokens";
import { tooltipStyleProps } from "services/constants/Styles";
import {
  GlobalBreadcrumb,
  globalTextFieldSx,
} from "components/DesignSystem/sharedUiPatterns";
import { IconSectionCard } from "components/DesignSystem/IconSectionCard";
import {
  PrimaryButton,
  SecondaryButton,
} from "components/CustomComponents/DesignSystemButtons";
import { organisationDetails } from "pages/Administration/SupportContacts/supportContactsData";
import { useSettingsForm } from "pages/Administration/PunchProcessing/useSettingsForm";
import { withScreenPermissions } from "components/DesignSystem/ScreenAccess";
import { SCREEN_ACCESS } from "services/utils/screenPermissions";
import {
  buildOrganisationSettingsDefaults,
  DATE_FORMAT_OPTIONS,
  LANGUAGE_OPTIONS,
  MINUTES_LIMIT,
  SCOPE_OPTIONS,
  TIME_ZONE_OPTIONS,
  validateOrganisationSettings,
} from "./organisationSettingsData";
import {
  isPunchProcessingResponse,
  organisationPunchProcessingUrl,
  organisationSettingsFromApi,
  organisationSettingsToApi,
} from "./organisationSettingsApi";

// The General card (Date Format, Time Zone, Default Language) is hidden for
// now. Set this to true to show it again; its fields, defaults and validation
// are all still in place.
const SHOW_GENERAL_CARD = false;

const GENERAL_SETTINGS = [
  {
    key: "dateFormat",
    label: "Date Format",
    helper: "Choose how dates are displayed across the system.",
    options: DATE_FORMAT_OPTIONS,
  },
  {
    key: "timeZone",
    label: "Time Zone",
    helper: "Set the default time zone for your organisation.",
    options: TIME_ZONE_OPTIONS,
  },
  {
    key: "language",
    label: "Default Language",
    helper: "Select the default language for the system.",
    options: LANGUAGE_OPTIONS,
  },
];

const minutesText = (value) => {
  const text = String(value ?? "").trim();
  if (!/^[0-9]+$/.test(text)) return "the set number of minutes";
  return Number(text) === 1 ? "1 minute" : Number(text) + " minutes";
};

// The two punch-gap rules, shown side by side. `example` and the chip
// captions follow the value currently entered.
const PUNCH_RULES = [
  {
    key: "minimumPunchDifference",
    tone: "green",
    label: "Minimum Same-Type Punch Difference",
    tooltip: "A punch of the same type that arrives sooner than this after the previous one is treated as a duplicate.",
    helper: "Minimum time difference between consecutive punches of the same type (IN → IN or OUT → OUT), regardless of device. Helps avoid duplicate punches.",
    example: (gap) =>
      "If set to " + gap + ", an IN punch followed by another IN punch within " +
      gap + " will be treated as a duplicate. The same rule applies to OUT → OUT.",
    pairs: [["IN", "IN"], ["OUT", "OUT"]],
  },
  {
    key: "minimumInOutPunchDifference",
    tone: "blue",
    label: "Minimum IN-OUT Punch Difference",
    tooltip: "An IN and an OUT punch closer together than this are flagged for review.",
    helper: "Minimum time between an IN punch and an OUT punch, in either order (IN → OUT or OUT → IN). Helps avoid implausibly short attendance intervals.",
    example: (gap) =>
      "If set to " + gap + ", an IN and OUT punch less than " + gap +
      " apart will be flagged for review. The same rule applies to OUT → IN.",
    pairs: [["IN", "OUT"], ["OUT", "IN"]],
  },
];

const DELAY_SETTING = {
  key: "attendanceProcessingDelay",
  label: "Attendance Processing Delay",
  helper: "Delay before processing new punches. Helps avoid processing incomplete data.",
  tooltip: "Attendance is recalculated this long after a punch is received. 0 processes it straight away.",
};

const TONES = {
  green: { background: "#EEF8F3", border: "#D5EEE2", accent: "#0F8A5F" },
  blue: { background: "#EEF4FD", border: "#D7E5FA", accent: "#2563EB" },
};

const PUNCH_CHIP = {
  IN: { backgroundColor: "#17A673", color: designColors.white },
  OUT: { backgroundColor: "#E4DDFB", color: "#5B3FC4" },
};

const SCOPE_SETTINGS = [
  {
    key: "manualProcessingScope",
    label: "Manual Processing Scope",
    helper: "Default company scope when adding or correcting manual entries.",
    tooltip: "The scope pre-selected for manual entries. It can be changed when performing the action.",
  },
  {
    key: "reprocessingScope",
    label: "Reprocessing Scope",
    helper: "Default company scope when reprocessing historical events (e.g. backfill, retry).",
    tooltip: "The scope pre-selected for reprocessing. It can be changed when performing the action.",
  },
];

const SCOPE_ICONS = { current: ApartmentOutlined, all: GroupsOutlined };

const rowDivider = "1px solid " + designColors.slate200;
const DANGER = "#DC2626";

// designTheme keeps MUI's default blue primary, so the focus ring and the
// selected option are set to the app's teal here.
const selectFieldSx = {
  ...globalTextFieldSx,
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: designColors.tealDark,
  },
};

const optionSx = {
  fontSize: 14,
  "&.Mui-selected, &.Mui-selected:hover, &.Mui-selected.Mui-focusVisible": {
    backgroundColor: designColors.tealTint,
    color: designColors.tealDark,
    fontWeight: 600,
  },
};

const SettingTitle = ({ label, tooltip, size = 15 }) => (
  <Mui.Stack direction="row" spacing={0.75} alignItems="center">
    <Mui.Typography sx={{ fontSize: size, fontWeight: 600, color: designColors.slate900 }}>
      {label}
    </Mui.Typography>
    {tooltip && (
      <Mui.Tooltip title={tooltip} arrow slotProps={tooltipStyleProps}>
        <InfoOutlined
          aria-label={"About " + label}
          sx={{ fontSize: 17, color: designColors.slate400, cursor: "pointer" }}
        />
      </Mui.Tooltip>
    )}
  </Mui.Stack>
);

// Small tinted square holding a row's icon.
const IconTile = ({ children }) => (
  <Mui.Box
    sx={{
      width: 44,
      height: 44,
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: designRadii.control,
      backgroundColor: designColors.tealTint,
      color: designColors.tealDark,
    }}
  >
    {children}
  </Mui.Box>
);

// One setting: optional icon, name, info tooltip and helper on the left,
// its control on the right.
const SettingRow = ({ icon, label, helper, tooltip, controlWidth = 6.5, divider = true, children }) => (
  <Mui.Grid
    container
    columnSpacing={3}
    rowSpacing={1.25}
    alignItems="center"
    sx={{ py: 2.5, borderBottom: divider ? rowDivider : "none" }}
  >
    <Mui.Grid item xs={12} md={12 - controlWidth}>
      <Mui.Stack direction="row" spacing={2} alignItems="center">
        {icon && <IconTile>{icon}</IconTile>}
        <Mui.Box sx={{ minWidth: 0 }}>
          <SettingTitle label={label} tooltip={tooltip} />
          <Mui.Typography sx={{ fontSize: 13.5, color: designColors.slate500, mt: 0.25 }}>
            {helper}
          </Mui.Typography>
        </Mui.Box>
      </Mui.Stack>
    </Mui.Grid>
    <Mui.Grid item xs={12} md={controlWidth}>
      {children}
    </Mui.Grid>
  </Mui.Grid>
);

const PunchChip = ({ type }) => (
  <Mui.Box
    component="span"
    sx={{
      ...PUNCH_CHIP[type],
      px: 1.5,
      py: 0.4,
      minWidth: 44,
      textAlign: "center",
      borderRadius: 999,
      fontSize: 13,
      fontWeight: 700,
      letterSpacing: 0.3,
    }}
  >
    {type}
  </Mui.Box>
);

// One punch-gap rule: title, explanation, stepper, a worked example and the
// punch pairs it applies to.
const PunchRuleCard = ({ rule, value, onChange, error }) => {
  const tone = TONES[rule.tone];
  const gap = minutesText(value);
  return (
    <Mui.Box
      sx={{
        height: "100%",
        p: { xs: 2, md: 2.5 },
        borderRadius: designRadii.card,
        border: rowDivider,
        backgroundColor: designColors.white,
      }}
    >
      <SettingTitle label={rule.label} tooltip={rule.tooltip} size={16} />
      <Mui.Typography sx={{ fontSize: 13.5, color: designColors.slate500, mt: 0.5 }}>
        {rule.helper}
      </Mui.Typography>
      <Mui.Box sx={{ mt: 2 }}>
        <MinutesStepper
          label={rule.label}
          value={value}
          onChange={onChange}
          error={error}
          align="start"
        />
      </Mui.Box>
      <Mui.Stack
        direction="row"
        spacing={1.5}
        sx={{
          mt: 2,
          p: 2,
          borderRadius: designRadii.control,
          border: "1px solid " + tone.border,
          backgroundColor: tone.background,
        }}
      >
        <LightbulbOutlined sx={{ fontSize: 22, color: tone.accent, mt: 0.1 }} />
        <Mui.Box>
          <Mui.Typography sx={{ fontSize: 14, fontWeight: 600, color: tone.accent }}>
            Example
          </Mui.Typography>
          <Mui.Typography sx={{ fontSize: 13.5, color: designColors.slate700, mt: 0.25 }}>
            {rule.example(gap)}
          </Mui.Typography>
        </Mui.Box>
      </Mui.Stack>
      <Mui.Stack
        direction="row"
        divider={<Mui.Divider orientation="vertical" flexItem />}
        sx={{ mt: 2 }}
      >
        {rule.pairs.map(([from, to]) => (
          <Mui.Stack key={from + to} alignItems="center" spacing={1} sx={{ flex: 1, py: 0.5 }}>
            <Mui.Stack direction="row" alignItems="center" spacing={1.25}>
              <PunchChip type={from} />
              <ArrowForwardRounded sx={{ fontSize: 18, color: designColors.slate700 }} />
              <PunchChip type={to} />
            </Mui.Stack>
            <Mui.Typography sx={{ fontSize: 13, color: designColors.slate600, textAlign: "center" }}>
              At least {gap} apart
            </Mui.Typography>
          </Mui.Stack>
        ))}
      </Mui.Stack>
    </Mui.Box>
  );
};

const KEYPAD_KEYS = [0, 1, 2].flatMap((row) =>
  [0, 1, 2].map((column) => ({ x: 8 + column * 6, y: 30 + row * 5 })),
);

// A biometric attendance terminal: screen on top, keypad bottom left and a
// lit fingerprint reader bottom right.
const BiometricDeviceIcon = () => (
  <svg width="46" height="54" viewBox="0 0 46 54" role="img" aria-label="Biometric device">
    <rect x="1" y="1" width="44" height="52" rx="7" fill="#1F2937" />
    <rect x="3" y="3" width="40" height="48" rx="5" fill="#111827" />
    <rect x="7" y="7" width="32" height="17" rx="2.5" fill="#38BDF8" />
    <rect x="9" y="9" width="28" height="13" rx="1.5" fill="#0EA5E9" />
    <path d="M12 18 L17 13 L21 16 L27 11" stroke="#E0F2FE" strokeWidth="1.4" fill="none" strokeLinecap="round" />
    {KEYPAD_KEYS.map(({ x, y }) => (
      <rect key={x + "-" + y} x={x} y={y} width="4" height="3" rx="0.8" fill="#4B5563" />
    ))}
    <rect x="28" y="29" width="12" height="16" rx="6" fill="#064E3B" />
    <g fill="none" stroke="#34D399" strokeWidth="1.1" strokeLinecap="round">
      <path d="M31 34.5 a3 3 0 0 1 6 0 v4" />
      <path d="M32.5 35 a1.5 1.5 0 0 1 3 0 v5" />
      <path d="M34 35.5 v5.5" />
      <path d="M30.5 37.5 v2" />
    </g>
  </svg>
);

// Advice for sites where one biometric device records both IN and OUT.
const SingleDeviceNotice = () => (
  <Mui.Stack
    direction={{ xs: "column", md: "row" }}
    spacing={2}
    alignItems={{ md: "center" }}
    sx={{
      p: 2,
      borderRadius: designRadii.control,
      border: "1px solid #F8DDB0",
      backgroundColor: "#FFF8EC",
    }}
  >
    <Mui.Stack direction="row" spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
      <InfoOutlined sx={{ fontSize: 30, color: "#E59A1A", flexShrink: 0 }} />
      <Mui.Box>
        <Mui.Typography sx={{ fontSize: 14, fontWeight: 600, color: designColors.slate900 }}>
          Single Device Configuration
        </Mui.Typography>
        <Mui.Typography sx={{ fontSize: 13, color: designColors.slate600, mt: 0.25 }}>
          If the same biometric device is used for both IN and OUT punches,
          configure the same minimum time difference in both settings to ensure
          consistent punch processing (e.g. set both values to 55 minutes).
        </Mui.Typography>
      </Mui.Box>
    </Mui.Stack>
    <Mui.Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
      <Mui.Box sx={{ display: "flex", filter: "drop-shadow(0 2px 3px rgba(15, 23, 42, 0.25))" }}>
        <BiometricDeviceIcon />
      </Mui.Box>
      <Mui.Box sx={{ width: 40, borderTop: "2px dashed #E59A1A" }} />
      <Mui.Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          px: 1.5,
          py: 1,
          borderRadius: designRadii.control,
          border: "1px solid #F8DDB0",
          backgroundColor: designColors.white,
        }}
      >
        <SettingsOutlined sx={{ fontSize: 20, color: "#E59A1A" }} />
        <Mui.Typography sx={{ fontSize: 12.5, color: designColors.slate700, lineHeight: 1.35 }}>
          Use the same value
          <br />
          in both settings
        </Mui.Typography>
      </Mui.Stack>
    </Mui.Stack>
  </Mui.Stack>
);

const stepButtonSx = {
  width: 44,
  borderRadius: 0,
  color: designColors.slate700,
  "&:hover": { backgroundColor: designColors.slateBg },
};

// Whole minutes with minus / plus buttons either side and a fixed unit.
const MinutesStepper = ({ label, value, onChange, error, align = "end" }) => {
  const number = Number(value);
  const usable = String(value).trim() !== "" && Number.isInteger(number);
  const step = (change) => {
    const next = (usable ? number : 0) + change;
    onChange(String(Math.min(MINUTES_LIMIT, Math.max(0, next))));
  };
  return (
    <Mui.Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: align === "end" ? { md: "flex-end" } : "flex-start",
      }}
    >
      <Mui.Stack
        direction="row"
        sx={{
          width: "100%",
          maxWidth: 320,
          height: 44,
          overflow: "hidden",
          borderRadius: designRadii.control,
          border: "1px solid " + (error ? DANGER : designColors.slate300),
          backgroundColor: designColors.white,
        }}
      >
        <Mui.IconButton
          aria-label={"Decrease " + label}
          onClick={() => step(-1)}
          disabled={usable && number <= 0}
          sx={{ ...stepButtonSx, borderRight: rowDivider }}
        >
          <RemoveRounded fontSize="small" />
        </Mui.IconButton>
        <Mui.InputBase
          value={value}
          onChange={(event) => onChange(event.target.value.replace(/[^0-9]/g, ""))}
          inputProps={{
            "aria-label": label,
            inputMode: "numeric",
            style: { textAlign: "center" },
          }}
          sx={{ flex: 1, minWidth: 0, fontSize: 14, color: designColors.slate900 }}
        />
        <Mui.IconButton
          aria-label={"Increase " + label}
          onClick={() => step(1)}
          disabled={usable && number >= MINUTES_LIMIT}
          sx={{ ...stepButtonSx, borderLeft: rowDivider }}
        >
          <AddRounded fontSize="small" />
        </Mui.IconButton>
        <Mui.Box
          sx={{
            px: 2,
            display: "flex",
            alignItems: "center",
            fontSize: 14,
            color: designColors.slate700,
            backgroundColor: designColors.slateBg,
            borderLeft: rowDivider,
          }}
        >
          minutes
        </Mui.Box>
      </Mui.Stack>
      {error && (
        <Mui.FormHelperText error sx={{ mt: 0.75, fontSize: 12.5 }}>
          {error}
        </Mui.FormHelperText>
      )}
    </Mui.Box>
  );
};

// Two selectable cards behaving as one radio group.
const ScopeCards = ({ label, value, onChange }) => (
  <Mui.Box
    role="radiogroup"
    aria-label={label}
    sx={{
      display: "grid",
      gap: 1.25,
      gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
    }}
  >
    {SCOPE_OPTIONS.map((option) => {
      const selected = option.value === value;
      const disabled = Boolean(option.comingSoon);
      const Icon = SCOPE_ICONS[option.value];
      return (
        <Mui.ButtonBase
          key={option.value}
          role="radio"
          aria-checked={selected}
          aria-disabled={disabled}
          aria-label={label + ": " + option.title + (disabled ? " (Coming soon)" : "")}
          disabled={disabled}
          onClick={() => onChange(option.value)}
          sx={{
            "&.Mui-disabled": {
              opacity: 0.6,
              cursor: "not-allowed",
              pointerEvents: "auto",
              backgroundColor: designColors.slate50 || "#F8FAFC",
              borderColor: designColors.slate200,
            },
            "&.Mui-disabled:hover": { borderColor: designColors.slate200 },
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "flex-start",
            textAlign: "left",
            gap: 1,
            p: 1.5,
            borderRadius: designRadii.card,
            border:
              "1.5px solid " +
              (selected ? designColors.tealDark : designColors.slate200),
            backgroundColor: selected ? designColors.tealTint : designColors.white,
            "&:hover": { borderColor: designColors.tealDark },
            "&.Mui-focusVisible": {
              outline: "2px solid " + designColors.tealDark,
              outlineOffset: 2,
            },
          }}
        >
          <Mui.Radio
            checked={selected}
            size="small"
            tabIndex={-1}
            disableRipple
            inputProps={{ "aria-hidden": true }}
            sx={{
              p: 0,
              mt: 0.25,
              pointerEvents: "none",
              color: designColors.slate400,
              "&.Mui-checked": { color: designColors.tealDark },
            }}
          />
          <Icon sx={{ fontSize: 24, mt: 0.1, color: designColors.slate600 }} />
          <Mui.Box sx={{ minWidth: 0 }}>
            <Mui.Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Mui.Typography
                sx={{ fontSize: 14, fontWeight: 500, color: designColors.slate900 }}
              >
                {option.title}
              </Mui.Typography>
              {disabled && (
                <Mui.Chip
                  label="Coming soon"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: 11,
                    fontWeight: 600,
                    color: designColors.slate600,
                    backgroundColor: designColors.slate200,
                  }}
                />
              )}
            </Mui.Box>
            <Mui.Typography sx={{ ...designType.helperText, mt: 0.25 }}>
              {option.text}
            </Mui.Typography>
          </Mui.Box>
        </Mui.ButtonBase>
      );
    })}
  </Mui.Box>
);

// Administration > Organisation > Settings: organisation-wide settings in
// three cards (General, Punch Processing, Processing Scope), one row per
// setting, with Cancel / Save Changes for the whole page at the foot.
const ignoreChange = () => {};

function OrganisationSettings({ permissions }) {
  // Without the edit permission the settings are shown locked, with no
  // Cancel or Save. This screen has no delete action.
  const canEdit = permissions?.canEdit !== false;
  const history = useHistory();
  const { domain } = useSelector((state) => state.tokenReducer);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  // No saved record yet (is_default) -> first save is a POST, later ones PUT.
  const [isDefault, setIsDefault] = useState(true);
  // The save response carries its own message; show that in the toast.
  const savedMessageRef = useRef("");
  const savedMessage = useCallback(
    () => savedMessageRef.current || "Organisation settings saved.",
    [],
  );
  const { form, errors, isDirty, isSaving, setValue: changeValue, save, discard, replaceValues } = useSettingsForm({
    buildDefaults: buildOrganisationSettingsDefaults,
    validate: validateOrganisationSettings,
    successMessage: savedMessage,
    // The field shows its own error under the control, so no toast as well.
    toastOnInvalid: false,
    onSave: async (values) => {
      const result = await customAPI(
        organisationPunchProcessingUrl(domain),
        isDefault ? "POST" : "PUT",
        organisationSettingsToApi(values),
      );
      if (!result?.ok) return false;
      setIsDefault(false);
      const data = result.responseDetails;
      savedMessageRef.current =
        typeof data?.message === "string" ? data.message.trim() : "";
      return isPunchProcessingResponse(data)
        ? organisationSettingsFromApi(data, values)
        : values;
    },
  });

  useEffect(() => {
    let active = true;
    const fail = (message) => {
      if (!active) return;
      setLoadError(message || "Organisation settings could not be loaded.");
      setLoading(false);
    };
    setLoading(true);
    setLoadError("");
    customGetAPI(organisationPunchProcessingUrl(domain))
      .then((result) => {
        if (!active) return;
        if (!result?.ok || !isPunchProcessingResponse(result.responseDetails)) {
          fail(result?.message);
          return;
        }
        replaceValues(
          organisationSettingsFromApi(
            result.responseDetails,
            buildOrganisationSettingsDefaults(),
          ),
        );
        setIsDefault(result.responseDetails.is_default === true);
        setLoading(false);
      })
      .catch((error) => fail(error.message));
    return () => {
      active = false;
    };
  }, [domain, reloadKey, replaceValues]);

  if (loading) return <Loader info="Loading..." />;
  if (loadError) {
    return (
      <Mui.Alert
        severity="error"
        sx={{ m: 3 }}
        action={
          <Mui.Button color="inherit" onClick={() => setReloadKey((value) => value + 1)}>
            Retry
          </Mui.Button>
        }
      >
        {loadError}
      </Mui.Alert>
    );
  }

  const setValue = canEdit ? changeValue : ignoreChange;

  return (
    <Mui.ThemeProvider theme={designTheme}>
      <Mui.Box sx={{ ...designShell.pageSurface, p: { xs: 2, md: 3 } }}>
        <Mui.Stack spacing={2}>
          <GlobalBreadcrumb
            trail={[
              {
                label: "Administration",
                onClick: () => history.push("/admin/administration/user"),
              },
              {
                label: "Organisation",
                onClick: () =>
                  history.push("/admin/administration/organisation/details"),
              },
              { label: "Settings" },
            ]}
          />
          <Mui.Box>
            <Mui.Typography component="h1" sx={designType.pageTitle}>
              Organisation Settings
            </Mui.Typography>
            <Mui.Typography sx={designType.pageSubtitle}>
              Configure {SHOW_GENERAL_CARD ? "general settings, " : ""}attendance punch
              processing and related behaviour for {organisationDetails.organisationName}.
              These settings apply across all companies and users in this
              organisation.
            </Mui.Typography>
          </Mui.Box>

          {/* `inert` keeps mouse and keyboard out of every control while the
              screen is view-only. */}
          <Mui.Stack
            spacing={2}
            {...(canEdit ? {} : { inert: "" })}
            sx={canEdit ? undefined : { pointerEvents: "none" }}
          >
          {SHOW_GENERAL_CARD && (
          <IconSectionCard
            icon={<SettingsOutlined />}
            title="General"
            subtitle="Set the basic configuration for your organisation."
          >
            <Mui.Box sx={{ borderTop: rowDivider }}>
              {GENERAL_SETTINGS.map((setting) => (
                <SettingRow key={setting.key} label={setting.label} helper={setting.helper}>
                  <Mui.TextField
                    select
                    fullWidth
                    size="small"
                    value={form[setting.key]}
                    onChange={(event) => setValue(setting.key, event.target.value)}
                    error={Boolean(errors[setting.key])}
                    helperText={errors[setting.key]}
                    inputProps={{ "aria-label": setting.label }}
                    sx={selectFieldSx}
                  >
                    {setting.options.map((option) => (
                      <Mui.MenuItem key={option.value} value={option.value} sx={optionSx}>
                        {option.label}
                      </Mui.MenuItem>
                    ))}
                  </Mui.TextField>
                </SettingRow>
              ))}
            </Mui.Box>
          </IconSectionCard>
          )}

          <IconSectionCard
            icon={<AccessTimeOutlined />}
            title="Punch Processing"
            subtitle="Configure timing and processing behaviour for attendance punches."
          >
            <Mui.Grid container spacing={2}>
              {PUNCH_RULES.map((rule) => (
                <Mui.Grid item xs={12} md={6} key={rule.key}>
                  <PunchRuleCard
                    rule={rule}
                    value={form[rule.key]}
                    onChange={(value) => setValue(rule.key, value)}
                    error={errors[rule.key]}
                  />
                </Mui.Grid>
              ))}
            </Mui.Grid>
            <Mui.Box sx={{ mt: 2 }}>
              <SingleDeviceNotice />
            </Mui.Box>
            <Mui.Box sx={{ mt: 1 }}>
              <SettingRow
                icon={<AccessTimeOutlined />}
                label={DELAY_SETTING.label}
                helper={DELAY_SETTING.helper}
                tooltip={DELAY_SETTING.tooltip}
                controlWidth={5}
              >
                <MinutesStepper
                  label={DELAY_SETTING.label}
                  value={form[DELAY_SETTING.key]}
                  onChange={(value) => setValue(DELAY_SETTING.key, value)}
                  error={errors[DELAY_SETTING.key]}
                />
              </SettingRow>
              <SettingRow
                icon={<EditOutlined />}
                label="Manual Changes"
                helper="Manual corrections and entries are processed immediately."
                tooltip="This behaviour is fixed and cannot be changed."
                controlWidth={5}
                divider={false}
              >
                <Mui.Box sx={{ display: "flex", justifyContent: { md: "flex-end" } }}>
                  <Mui.Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    aria-label="Manual Changes: Process Immediately (fixed)"
                    sx={{
                      width: "100%",
                      maxWidth: 320,
                      height: 44,
                      px: 1.5,
                      borderRadius: designRadii.control,
                      border: "1px solid " + designColors.slate200,
                      backgroundColor: designColors.slateBg,
                      color: designColors.slate600,
                    }}
                  >
                    <BoltOutlined fontSize="small" />
                    <Mui.Typography sx={{ flex: 1, fontSize: 14, color: designColors.slate700 }}>
                      Process Immediately
                    </Mui.Typography>
                    <LockOutlined fontSize="small" />
                  </Mui.Stack>
                </Mui.Box>
              </SettingRow>
            </Mui.Box>
          </IconSectionCard>

          <IconSectionCard
            icon={<GroupsOutlined />}
            title="Processing Scope"
            subtitle="Set the default company scope for manual actions and reprocessing. You can override these when performing the action."
          >
            <Mui.Box sx={{ borderTop: rowDivider }}>
              {SCOPE_SETTINGS.map((setting) => (
                <SettingRow
                  key={setting.key}
                  label={setting.label}
                  helper={setting.helper}
                  tooltip={setting.tooltip}
                  controlWidth={8}
                >
                  <ScopeCards
                    label={setting.label}
                    value={form[setting.key]}
                    onChange={(value) => setValue(setting.key, value)}
                  />
                  {errors[setting.key] && (
                    <Mui.FormHelperText error sx={{ mt: 0.75, fontSize: 12.5 }}>
                      {errors[setting.key]}
                    </Mui.FormHelperText>
                  )}
                </SettingRow>
              ))}
            </Mui.Box>
          </IconSectionCard>
          </Mui.Stack>

          {canEdit && <Mui.Stack
            direction={{ xs: "column-reverse", sm: "row" }}
            justifyContent="flex-end"
            spacing={1.5}
          >
            <SecondaryButton
              onClick={discard}
              disabled={!isDirty || isSaving}
              sx={{ minWidth: HEADER_ACTION_BUTTON_WIDTH }}
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              startIcon={<SaveOutlined />}
              onClick={save}
              disabled={!isDirty || isSaving}
              sx={{ minWidth: HEADER_ACTION_BUTTON_WIDTH }}
            >
              Save Changes
            </PrimaryButton>
          </Mui.Stack>}
        </Mui.Stack>
      </Mui.Box>
    </Mui.ThemeProvider>
  );
}

export default withScreenPermissions(OrganisationSettings, SCREEN_ACCESS.organisationSettings, "Organisation Settings");
